import { useState } from "react";
import { RegisterUser } from "../../Services/Operations/ProductAPI";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// ── Shared icons ──────────────────────────────────────────────
const eyeOpen = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const eyeClosed = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const mailIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);
const lockIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const userIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);
const phoneIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16l.92.92z"/>
  </svg>
);
const calendarIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const logoMark = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L2 7l10 5 10-5-10-5z" fill="white" opacity="0.9"/>
    <path d="M2 17l10 5 10-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.6"/>
    <path d="M2 12l10 5 10-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
  </svg>
);
const spinnerSvg = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite"/>
    </path>
  </svg>
);

// ── Shared design tokens (mirrors LoginPage exactly) ──────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    padding: "2rem 1rem",
  },
  card: {
    background: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 8px 40px rgba(0,0,0,0.10), 0 2px 12px rgba(0,0,0,0.06)",
    padding: "48px 44px 44px",
    width: "100%",
    maxWidth: "460px",
  },
  logoWrap: {
    width: "44px", height: "44px", borderRadius: "12px",
    background: "#18181b", display: "flex",
    alignItems: "center", justifyContent: "center", marginBottom: "28px",
  },
  heading: { fontSize: "24px", fontWeight: "700", color: "#111113", margin: "0 0 6px", letterSpacing: "-0.5px" },
  subheading: { fontSize: "14px", color: "#9ca3af", margin: "0 0 30px", fontWeight: "400" },
  fieldGroup: { marginBottom: "14px" },
  label: {
    display: "block", fontSize: "12.5px", fontWeight: "600",
    color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: "7px",
  },
  inputWrapper: { position: "relative", display: "flex", alignItems: "center" },
  inputIcon: {
    position: "absolute", left: "14px", display: "flex",
    alignItems: "center", pointerEvents: "none", transition: "color 0.2s",
  },
  eyeBtn: {
    position: "absolute", right: "14px", background: "none",
    border: "none", cursor: "pointer", color: "#9ca3af", display: "flex", alignItems: "center", padding: "0",
  },
  divider: { display: "flex", alignItems: "center", gap: "12px", margin: "16px 0" },
  dividerLine: { flex: 1, height: "1px", background: "#f3f4f6" },
  dividerText: { fontSize: "11px", color: "#d1d5db", fontWeight: "600", letterSpacing: "0.06em", textTransform: "uppercase" },
  signinRow: { textAlign: "center", fontSize: "13.5px", color: "#9ca3af", marginTop: "20px" },
  signinLink: { color: "#18181b", fontWeight: "600", cursor: "pointer", marginLeft: "4px" },
};

// ── Reusable field input ──────────────────────────────────────
function Field({ label, icon, iconActive, type = "text", placeholder, value, onChange, rightSlot, inputStyle = {} }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={S.fieldGroup}>
      <label style={S.label}>{label}</label>
      <div style={S.inputWrapper}>
        <span style={{ ...S.inputIcon, color: focused ? "#18181b" : "#9ca3af" }}>{icon}</span>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          required
          style={{
            width: "100%", height: "48px",
            border: focused ? "1.5px solid #18181b" : "1.5px solid #e5e7eb",
            borderRadius: "10px",
            background: focused ? "#fafafa" : "#f9fafb",
            padding: rightSlot ? "0 44px 0 44px" : "0 14px 0 44px",
            fontSize: "15px", color: "#111113", outline: "none",
            transition: "border 0.2s, background 0.2s",
            boxSizing: "border-box", fontFamily: "inherit",
            ...inputStyle,
          }}
        />
        {rightSlot}
      </div>
    </div>
  );
}

// ── Password strength meter ───────────────────────────────────
function strengthOf(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"];
const strengthColor = ["", "#ef4444", "#f59e0b", "#3b82f6", "#22c55e"];

function StrengthBar({ password }) {
  const s = strengthOf(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: "8px" }}>
      <div style={{ display: "flex", gap: "4px", marginBottom: "4px" }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            flex: 1, height: "3px", borderRadius: "2px",
            background: i <= s ? strengthColor[s] : "#e5e7eb",
            transition: "background 0.3s",
          }} />
        ))}
      </div>
      <span style={{ fontSize: "11.5px", color: strengthColor[s], fontWeight: "600" }}>{strengthLabel[s]}</span>
    </div>
  );
}

const Register = () => {
    const navigate=useNavigate()
 const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    birth_date: "", password: "", password_confirmation: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async(e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.password_confirmation) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);

    try {
        const res=await RegisterUser(form)
        console.log(res);
        if(res.status===201){
            setLoading(false);
        setSubmitted(true)
            toast.success("New user created");
            navigate("/login")
        }
        
    } catch (error) {
        console.log(error);
        setLoading(false);
        setSubmitted(true)
    }
   
  };

  const passwordsMatch = form.password_confirmation
    ? form.password === form.password_confirmation
    : null;

  const submitBtn = {
    width: "100%", height: "50px",
    background: loading ? "#52525b" : "#18181b",
    color: "#ffffff", border: "none", borderRadius: "10px",
    fontSize: "15px", fontWeight: "600",
    cursor: loading ? "not-allowed" : "pointer",
    marginTop: "24px", letterSpacing: "0.01em",
    transition: "background 0.2s",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
    fontFamily: "inherit",
  };

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={S.card}>

        {/* Logo */}
        <div style={S.logoWrap}>{logoMark}</div>

        {submitted ? (
          <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
            <div style={{
              width: "52px", height: "52px", borderRadius: "50%",
              background: "#f0fdf4", display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 16px",
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <p style={{ fontSize: "20px", fontWeight: "700", color: "#111113", margin: "0 0 8px" }}>Account created!</p>
            <p style={{ fontSize: "14px", color: "#9ca3af", margin: 0 }}>Welcome aboard. You can now sign in.</p>
          </div>
        ) : (
          <>
            <h1 style={S.heading}>Create account</h1>
            <p style={S.subheading}>Fill in the details below to get started</p>

            {error && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                borderRadius: "10px", padding: "10px 14px",
                fontSize: "13.5px", color: "#dc2626",
                marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px",
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} autoComplete="off">

              {/* Full Name */}
              <Field
                label="Full name"
                icon={userIcon}
                placeholder="John Doe"
                value={form.full_name}
                onChange={set("full_name")}
              />

              {/* Two-column row: Phone + Birth date */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                {/* Phone */}
                <div style={S.fieldGroup}>
                  <label style={S.label}>Phone</label>
                  <div style={S.inputWrapper}>
                    <span style={{ ...S.inputIcon, color: "#9ca3af" }}>{phoneIcon}</span>
                    <input
                      type="tel"
                      placeholder="9874273250"
                      value={form.phone}
                      onChange={set("phone")}
                      required
                      style={{
                        width: "100%", height: "48px",
                        border: "1.5px solid #e5e7eb", borderRadius: "10px",
                        background: "#f9fafb", padding: "0 14px 0 44px",
                        fontSize: "15px", color: "#111113", outline: "none",
                        boxSizing: "border-box", fontFamily: "inherit",
                        transition: "border 0.2s, background 0.2s",
                      }}
                      onFocus={e => { e.target.style.border = "1.5px solid #18181b"; e.target.style.background = "#fafafa"; }}
                      onBlur={e => { e.target.style.border = "1.5px solid #e5e7eb"; e.target.style.background = "#f9fafb"; }}
                    />
                  </div>
                </div>

                {/* Birth Date */}
                <div style={S.fieldGroup}>
                  <label style={S.label}>Birth date</label>
                  <div style={S.inputWrapper}>
                    <span style={{ ...S.inputIcon, color: "#9ca3af" }}>{calendarIcon}</span>
                    <input
                      type="date"
                      value={form.birth_date}
                      onChange={set("birth_date")}
                      required
                      style={{
                        width: "100%", height: "48px",
                        border: "1.5px solid #e5e7eb", borderRadius: "10px",
                        background: "#f9fafb", padding: "0 10px 0 44px",
                        fontSize: "14px", color: form.birth_date ? "#111113" : "#9ca3af",
                        outline: "none", boxSizing: "border-box", fontFamily: "inherit",
                        transition: "border 0.2s, background 0.2s",
                      }}
                      onFocus={e => { e.target.style.border = "1.5px solid #18181b"; e.target.style.background = "#fafafa"; }}
                      onBlur={e => { e.target.style.border = "1.5px solid #e5e7eb"; e.target.style.background = "#f9fafb"; }}
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <Field
                label="Email"
                icon={mailIcon}
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={set("email")}
              />

              {/* Divider */}
              <div style={S.divider}>
                <div style={S.dividerLine}/>
                <span style={S.dividerText}>Security</span>
                <div style={S.dividerLine}/>
              </div>

              {/* Password */}
              <div style={S.fieldGroup}>
                <label style={S.label}>Password</label>
                <div style={S.inputWrapper}>
                  <span style={{ ...S.inputIcon, color: "#9ca3af" }}>{lockIcon}</span>
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder="Min. 8 characters"
                    value={form.password}
                    onChange={set("password")}
                    required
                    style={{
                      width: "100%", height: "48px",
                      border: "1.5px solid #e5e7eb", borderRadius: "10px",
                      background: "#f9fafb", padding: "0 44px 0 44px",
                      fontSize: "15px", color: "#111113", outline: "none",
                      boxSizing: "border-box", fontFamily: "inherit",
                      transition: "border 0.2s, background 0.2s",
                    }}
                    onFocus={e => { e.target.style.border = "1.5px solid #18181b"; e.target.style.background = "#fafafa"; }}
                    onBlur={e => { e.target.style.border = "1.5px solid #e5e7eb"; e.target.style.background = "#f9fafb"; }}
                  />
                  <button type="button" style={S.eyeBtn} onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                    {showPass ? eyeClosed : eyeOpen}
                  </button>
                </div>
                <StrengthBar password={form.password} />
              </div>

              {/* Confirm password */}
              <div style={S.fieldGroup}>
                <label style={S.label}>Confirm password</label>
                <div style={S.inputWrapper}>
                  <span style={{ ...S.inputIcon, color: "#9ca3af" }}>{lockIcon}</span>
                  <input
                    type={showConfirm ? "text" : "password"}
                    placeholder="Repeat your password"
                    value={form.password_confirmation}
                    onChange={set("password_confirmation")}
                    required
                    style={{
                      width: "100%", height: "48px",
                      border: passwordsMatch === false
                        ? "1.5px solid #ef4444"
                        : passwordsMatch === true
                          ? "1.5px solid #22c55e"
                          : "1.5px solid #e5e7eb",
                      borderRadius: "10px",
                      background: "#f9fafb", padding: "0 68px 0 44px",
                      fontSize: "15px", color: "#111113", outline: "none",
                      boxSizing: "border-box", fontFamily: "inherit",
                      transition: "border 0.2s",
                    }}
                  />
                  {/* Match indicator */}
                  {passwordsMatch !== null && (
                    <span style={{ position: "absolute", right: "44px", display: "flex", alignItems: "center" }}>
                      {passwordsMatch ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      )}
                    </span>
                  )}
                  <button type="button" style={S.eyeBtn} onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                    {showConfirm ? eyeClosed : eyeOpen}
                  </button>
                </div>
                {passwordsMatch === false && (
                  <p style={{ fontSize: "12px", color: "#ef4444", margin: "5px 0 0", fontWeight: "500" }}>
                    Passwords don't match
                  </p>
                )}
              </div>

              <button type="submit" style={submitBtn} disabled={loading}>
                {loading ? <>{spinnerSvg} Creating account…</> : "Create account"}
              </button>
            </form>

            <p style={S.signinRow}>
              Already have an account?
              <span style={S.signinLink}>Sign in</span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default Register
